import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { useNexusPageDetails } from '../context/nexusPage';
import { buildPageContext } from '../context/pageContext';

const HEARTBEAT_MS = 60000;
const DEBOUNCE_MS = 800;

// Identifiant de l'onglet, gardé le temps de l'onglet (sessionStorage) : chaque onglet a sa ligne côté serveur.
const tabId = () => {
  try {
    let value = sessionStorage.getItem('df-tab-id');
    if (!value) {
      value = `t${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem('df-tab-id', value);
    }
    return value;
  } catch {
    return 'tab-sans-stockage';
  }
};

// Ce que l'onglet affiche (même contexte que celui joint aux messages de Nexus), publié pour une IA externe branchée
// par le MCP (outil moi__ecran) : à chaque changement de page ou de sélection, au premier plan ou non, et toutes les
// minutes. Un onglet qui se ferme le dit.
const ScreenContextPublisher = () => {
  const location = useLocation();
  const details = useNexusPageDetails();
  const id = useMemo(tabId, []);
  const context = useMemo(() => {
    const base = buildPageContext(location);
    const { timestamp, ...stable } = base;
    return {
      ...stable,
      title: document.title,
      description: details?.description || base.description,
      extra: { ...base.extra, ...(details?.extra || {}) },
    };
  }, [location, details]);
  const latest = useRef(context);
  latest.current = context;

  useEffect(() => {
    const send = () => apiService.post('/presence/screen', {
      tab_id: id, context: latest.current, visible: document.visibilityState === 'visible', focused: document.hasFocus(),
    }).catch(() => {});
    const timer = setTimeout(send, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [context, id]);

  useEffect(() => {
    const send = () => apiService.post('/presence/screen', {
      tab_id: id, context: latest.current, visible: document.visibilityState === 'visible', focused: document.hasFocus(),
    }).catch(() => {});
    const close = () => apiService.post('/presence/screen', { tab_id: id, closed: true }).catch(() => {});
    const interval = setInterval(send, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', send);
    window.addEventListener('focus', send);
    window.addEventListener('blur', send);
    window.addEventListener('pagehide', close);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', send);
      window.removeEventListener('focus', send);
      window.removeEventListener('blur', send);
      window.removeEventListener('pagehide', close);
    };
  }, [id]);

  return null;
};

export default ScreenContextPublisher;
